import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle } from '@mui/icons-material';

export type StepProgressBarProps = {
  currentStepIndex: number;
  enableTransitionDelay?: boolean;
};

type StepGroup = {
  label: string;
  stepsPortion: [number, number];
};

const stepGroups: StepGroup[] = [
  {
    label: 'Expertise',
    stepsPortion: [ 0, 4 ],
  },
  {
    label: 'About You',
    stepsPortion: [ 5, 11 ],
  },
  {
    label: 'More Info',
    stepsPortion: [ 12, 13 ],
  },
  {
    label: 'Photo',
    stepsPortion: [ 14, 14 ],
  }
];

export const StepProgressBar: React.VFC<StepProgressBarProps> = props => {
  const { classes, cx } = useStepProgressBarStyles();

  return (
    <Box className={classes.progressBar} role="progressbar">
      {stepGroups.map((group) => {
        const firstStep = group.stepsPortion[0];
        const lastStep = group.stepsPortion[1];
        const isCompleted = props.currentStepIndex > lastStep;
        const isNotStarted = props.currentStepIndex < firstStep;
        let stepPercentage = 0;

        if (isCompleted) {
          stepPercentage = 100;
        } else if (isNotStarted) {
          stepPercentage = 0;
        } else {
          stepPercentage = ((props.currentStepIndex - firstStep + 1) / (lastStep - firstStep + 1)) * 100;
        }

        return (
          <Box key={group.label} className={classes.stepGroup}>
            <Box className={classes.stepGroupLabel}>
              {isCompleted && <CheckCircle fontSize="small" className={classes.stepGroupLabelIcon} />}
              <Typography variant="overline" color={isNotStarted ? 'textSecondary' : 'textPrimary'}>{group.label}</Typography>
            </Box>
            <Box className={cx(classes.stepGroupProgress, props.enableTransitionDelay && 'delay')} style={{ width: `${stepPercentage}%` }} />
          </Box>
        );
      })}
    </Box>
  );
};

const useStepProgressBarStyles = makeStyles({ name: 'StepProgressBar' })(theme => ({
  progressBar: {
    display: 'flex',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  stepGroup: {
    flex: 1,
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    position: 'relative',
  },
  stepGroupLabel: {
    zIndex: 2,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  stepGroupLabelIcon: {
    position: 'absolute',
    right: `calc(100% + ${theme.spacing(0.5)})`,
  },
  stepGroupProgress: {
    position: 'absolute',
    height: '100%',
    width: 0,
    top: 0,
    left: 0,
    bottom: 0,
    backgroundColor: theme.palette.secondary.light,
    transition: 'width 0.9s ease',
    zIndex: 1,

    '&.delay': {
      transitionDelay: '500ms',
    }
  }
}));
